/**
 * StarRating component
 * Renders N filled stars out of a maximum (default 5) for a given numeric rating.
 * Display-only — no user input. Used by SessionCard (student history) and US-019
 * (parent session list). This is the shared primitive described in the solution
 * design new_components list.
 *
 * Implements US-018 T3.
 * DD-C: Student variant shows stars only; N-of-M fraction is parent-only (US-019).
 * CCM-03: No price, coin, or financial data is ever passed to or rendered by this
 * component — it receives only a numeric rating.
 */

import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Props for the StarRating component.
 */
export interface StarRatingProps {
  /**
   * Star rating value. The frontend must NEVER compute this — it is received from
   * the backend (derive_star_rating, TS-014 / DD-003). null | undefined | <=0
   * causes the component to render nothing.
   */
  rating: number | null | undefined;
  /** Maximum number of stars to render (default 5). */
  max?: number;
  /** Size variant: 'sm' (h-3.5 w-3.5) or 'md' (h-4 w-4, default). */
  size?: 'sm' | 'md';
  /** Additional className forwarded to the wrapper element. */
  className?: string;
}

/**
 * StarRating — displays filled and empty star icons for a read-only rating.
 *
 * Renders nothing when rating is null, undefined, or <= 0 (non-rated sessions).
 * Clamps the filled count to [0, max] defensively.
 *
 * Accessibility: the wrapper has role='img' and an aria-label describing the rating
 * so screen readers do not read each star icon individually.
 */
export function StarRating({
  rating,
  max = 5,
  size = 'md',
  className,
}: StarRatingProps) {
  // Render nothing for non-rated sessions (null, undefined, or zero/negative).
  if (rating == null || rating <= 0) {
    return null;
  }

  // Clamp filled count into [0, max] defensively.
  const filled = Math.max(0, Math.min(max, Math.round(rating)));

  const sizeClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <span
      role="img"
      aria-label={`${filled} out of ${max} stars`}
      className={cn('flex items-center gap-0.5', className)}
    >
      {Array.from({ length: max }, (_, i) => {
        const isFilled = i < filled;
        return (
          <Star
            key={i}
            className={cn(
              sizeClass,
              isFilled
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300'
            )}
          />
        );
      })}
    </span>
  );
}
